import { useRef } from 'react';

import { chartConfig } from '@config/chart';
import type { AggregatedPosition } from '@hooks/usePositionAggregation';

import { YScale } from './types';

interface InteractionOverlayProps {
  innerWidth: number;
  innerHeight: number;
  margin: { top: number; bottom: number };
  yScale: YScale;
  strikePrices: number[];
  strikePrice?: number;
  isDraggingStrike: boolean;
  touchStartTime: React.MutableRefObject<number>;
  lastTouchDistance: React.MutableRefObject<{ x: number; y: number } | null>;
  lastUpdateTime: React.MutableRefObject<number>;
  containerRef: React.RefObject<HTMLDivElement>;
  aggregatedPositions: AggregatedPosition[];
  selectedPositionGroup?: AggregatedPosition | null;
  onStrikePriceSelect?: (price: number) => void;
  onPositionSelect?: (position: AggregatedPosition | null) => void;
  onStrikeDragUpdate: (
    effectivePrice: number,
    y: number,
    nearest: number,
    isSnapped?: boolean,
  ) => void;
  onStrikeDragEnd: () => void;
  onXScaleChange: (zoom: number) => void;
  onYZoomChange: (zoom: number, centerPrice?: number) => void;
  onPan?: (deltaX: number, deltaY: number) => void;
  onTradeZoneDeselect?: () => void;
}

export function InteractionOverlay({
  innerWidth,
  innerHeight,
  margin,
  yScale,
  strikePrices,
  strikePrice,
  isDraggingStrike,
  touchStartTime,
  lastTouchDistance,
  lastUpdateTime,
  containerRef,
  aggregatedPositions,
  selectedPositionGroup,
  onStrikePriceSelect,
  onPositionSelect,
  onStrikeDragUpdate,
  onStrikeDragEnd: _onStrikeDragEnd,
  onXScaleChange,
  onYZoomChange,
  onPan,
  onTradeZoneDeselect,
}: InteractionOverlayProps) {
  const _scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const panStartPos = useRef<{ x: number; y: number } | null>(null);
  const isPanning = useRef(false);

  return (
    <rect
      id="interaction-overlay"
      data-testid="chart-interaction-overlay"
      width={innerWidth}
      height={innerHeight}
      fill="transparent"
      style={{
        touchAction: 'manipulation',
        WebkitUserSelect: 'none',
        userSelect: 'none',
        pointerEvents: 'all',
      }}
      onMouseMove={e => {
        if (isDraggingStrike && strikePrices.length > 0) {
          const rect = containerRef.current?.getBoundingClientRect();
          if (rect) {
            const y = e.clientY - rect.top - margin.top;
            const rawPrice = yScale.invert(y);

            // Find nearest strike price from the valid strikes array
            const nearest = strikePrices.reduce((prev, curr) =>
              Math.abs(curr - rawPrice) < Math.abs(prev - rawPrice)
                ? curr
                : prev,
            );

            // Check if we're close enough to snap visually
            const nearestY = yScale(nearest);
            const shouldSnap =
              Math.abs(y - nearestY) <= chartConfig.interaction.snapThreshold;

            // Always follow finger for smooth movement, but snap visually when close
            const effectivePrice = shouldSnap ? nearest : rawPrice;

            onStrikeDragUpdate(effectivePrice, y, nearest, shouldSnap);
          }
        }
      }}
      onMouseUp={() => {
        if (isDraggingStrike) {
          // Mark the time when drag ended to prevent immediate trade zone deselection
          (window as any).lastDragEndTime = Date.now();
          // Note: Drag end strike selection is handled by global event listeners in useChartEffects
        }
      }}
      onClick={e => {
        const now = Date.now();
        const lastDeselection = (window as any).lastDeselectionTime ?? 0;
        const lastDragEnd = (window as any).lastDragEndTime ?? 0;

        if (!isDraggingStrike) {
          const rect = containerRef.current?.getBoundingClientRect();
          if (rect) {
            const y = e.clientY - rect.top - margin.top;

            // First, check for position proximity (higher priority)
            let nearestPosition = null;
            let minPositionDistance = Infinity;

            for (const position of aggregatedPositions) {
              const positionY = yScale(position.breakEvenPrice);
              const clickDistance = Math.abs(y - positionY);
              if (clickDistance < minPositionDistance) {
                minPositionDistance = clickDistance;
                nearestPosition = position;
              }
            }

            // Then check for strike proximity
            let nearestStrike = null;
            let minStrikeDistance = Infinity;

            for (const strike of strikePrices) {
              const strikeY = yScale(strike);
              const clickDistance = Math.abs(y - strikeY);
              if (clickDistance < minStrikeDistance) {
                minStrikeDistance = clickDistance;
                nearestStrike = strike;
              }
            }

            // Prioritize position selection if clicking within threshold pixels of a position
            if (
              nearestPosition &&
              minPositionDistance <=
                chartConfig.interaction.positionClickThreshold &&
              onPositionSelect
            ) {
              const isSelected =
                selectedPositionGroup?.id === nearestPosition.id;
              onPositionSelect(isSelected ? null : nearestPosition);
            }
            // If no position nearby, select the nearest strike
            // When no strike selected: select nearest (original behavior)
            // When strike already selected: only change if clicking close to a different strike
            else if (
              nearestStrike &&
              onStrikePriceSelect &&
              now - lastDeselection >
                chartConfig.interaction.debounce.deselection &&
              (!strikePrice ||
                minStrikeDistance <=
                  chartConfig.interaction.strikeSelectDistance)
            ) {
              onStrikePriceSelect(nearestStrike);
            }
            // If click is elsewhere and we have a strike selected, deselect trade zones
            // But don't deselect if we just finished a drag
            else if (
              minStrikeDistance >
                chartConfig.interaction.strikeDeselectDistance &&
              onTradeZoneDeselect &&
              now - lastDragEnd > chartConfig.interaction.debounce.postDrag
            ) {
              onTradeZoneDeselect();
            }
          }
        }
      }}
      onWheel={event => {
        if (isDraggingStrike) {
          return;
        }

        const { deltaY, deltaX } = event;

        // Detect touchpad vs mouse wheel
        // Touchpad: small delta values + pixel mode, Mouse wheel: large delta values
        const isTouchpad = Math.abs(deltaY) < 50 && event.deltaMode === 0;

        // Different sensitivity for touchpad vs mouse wheel
        const baseSensitivity = isTouchpad
          ? chartConfig.interaction.zoom.touchpadSensitivity
          : chartConfig.interaction.zoom.mouseSensitivity;

        // Increase sensitivity if shift is held for fine control
        const sensitivity = event.shiftKey
          ? baseSensitivity * chartConfig.interaction.zoom.shiftMultiplier
          : baseSensitivity;

        // Vertical zoom (Y axis) - zoom towards current price to keep it anchored
        if (Math.abs(deltaY) > Math.abs(deltaX)) {
          // Invert deltaY so wheel up = zoom in (feels more natural)
          const factor = deltaY < 0 ? 1 + sensitivity : 1 - sensitivity;

          // Always zoom around current price to keep it centered
          onYZoomChange(factor, undefined); // Pass undefined for centerPrice to use default
        }
        // Horizontal zoom (X axis) - proportionally scaled
        else {
          const xSensitivity = sensitivity * (isTouchpad ? 2 : 5); // Less aggressive for touchpad
          const factor = deltaX > 0 ? 1 - xSensitivity : 1 + xSensitivity;
          onXScaleChange(factor);
        }
      }}
      onTouchStart={event => {
        if (isDraggingStrike) {
          return;
        }

        // Handle single touch for strike selection or panning
        if (event.touches.length === 1) {
          const touch = event.touches[0];
          touchStartTime.current = Date.now();
          panStartPos.current = { x: touch.clientX, y: touch.clientY };
          isPanning.current = false;
          return;
        }

        // Handle two-finger pinch/zoom
        if (event.touches.length === 2) {
          // Cancel any ongoing pan
          panStartPos.current = null;
          isPanning.current = false;

          const touch1 = event.touches[0];
          const touch2 = event.touches[1];

          const xDistance = Math.abs(touch2.clientX - touch1.clientX);
          const yDistance = Math.abs(touch2.clientY - touch1.clientY);

          lastTouchDistance.current = { x: xDistance, y: yDistance };
          lastUpdateTime.current = Date.now();
        }
      }}
      onTouchMove={event => {
        // Handle strike dragging
        if (
          isDraggingStrike &&
          event.touches.length === 1 &&
          strikePrices.length > 0
        ) {
          const rect = containerRef.current?.getBoundingClientRect();
          if (rect) {
            const touch = event.touches[0];
            const y = touch.clientY - rect.top - margin.top;
            const rawPrice = yScale.invert(y);

            // Find nearest strike price
            const nearest = strikePrices.reduce((prev, curr) =>
              Math.abs(curr - rawPrice) < Math.abs(prev - rawPrice)
                ? curr
                : prev,
            );

            // Check if we're close enough to snap visually
            const nearestY = yScale(nearest);
            const shouldSnap =
              Math.abs(y - nearestY) <= chartConfig.interaction.snapThreshold;

            // Always follow finger for smooth movement, but snap visually when close
            const effectivePrice = shouldSnap ? nearest : rawPrice;

            onStrikeDragUpdate(effectivePrice, y, nearest, shouldSnap);
          }
          return;
        }

        // Skip zoom handling if dragging strike
        if (isDraggingStrike) {
          return;
        }

        // Handle one-finger panning
        if (event.touches.length === 1 && panStartPos.current && onPan) {
          const touch = event.touches[0];
          const deltaX = panStartPos.current.x - touch.clientX;
          const deltaY = panStartPos.current.y - touch.clientY;

          // Start panning if moved more than 5 pixels
          if (
            !isPanning.current &&
            (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5)
          ) {
            isPanning.current = true;
          }

          // Only pan if we've moved enough
          if (isPanning.current) {
            onPan(deltaX, deltaY);
            // Update pan start position for continuous panning
            panStartPos.current = { x: touch.clientX, y: touch.clientY };
          }
          return;
        }

        if (event.touches.length === 2 && lastTouchDistance.current) {
          const now = Date.now();
          // Throttle to max 60fps
          if (now - lastUpdateTime.current < 16) {
            return;
          }

          const touch1 = event.touches[0];
          const touch2 = event.touches[1];

          const xDistance = Math.abs(touch2.clientX - touch1.clientX);
          const yDistance = Math.abs(touch2.clientY - touch1.clientY);

          // Calculate zoom factors with reasonable bounds
          const xFactor = Math.min(
            Math.max(xDistance / lastTouchDistance.current.x, 0.8),
            1.2,
          );
          onXScaleChange(xFactor);

          const yFactor = Math.min(
            Math.max(yDistance / lastTouchDistance.current.y, 0.8),
            1.2,
          );
          onYZoomChange(yFactor, undefined); // Pass undefined for centerPrice to use default

          // Update stored distances
          lastTouchDistance.current = { x: xDistance, y: yDistance };
          lastUpdateTime.current = now;
        }
      }}
      onTouchEnd={event => {
        // Always reset strike dragging first
        if (isDraggingStrike) {
          // Mark the time when drag ended to prevent immediate trade zone deselection
          (window as any).lastDragEndTime = Date.now();
          // Note: Drag end strike selection is handled by global event listeners in useChartEffects
          return;
        }

        // Check if was panning - if so, don't treat as tap
        const wasPanning = isPanning.current;

        // Reset pan state
        panStartPos.current = null;
        isPanning.current = false;

        // Check for quick tap (not a drag, zoom, or pan)
        if (
          !wasPanning &&
          event.changedTouches.length === 1 &&
          touchStartTime.current > 0 &&
          Date.now() - touchStartTime.current < 200 // Quick tap under 200ms
        ) {
          const now = Date.now();
          const lastDragEnd = (window as any).lastDragEndTime ?? 0;
          const rect = containerRef.current?.getBoundingClientRect();
          if (rect) {
            const touch = event.changedTouches[0];
            const y = touch.clientY - rect.top - margin.top;

            // First, check for position proximity (higher priority)
            let nearestPosition = null;
            let minPositionDistance = Infinity;

            for (const position of aggregatedPositions) {
              const positionY = yScale(position.breakEvenPrice);
              const tapDistance = Math.abs(y - positionY);
              if (tapDistance < minPositionDistance) {
                minPositionDistance = tapDistance;
                nearestPosition = position;
              }
            }

            // Then check for strike proximity
            let nearestStrike = null;
            let minStrikeDistance = Infinity;

            for (const strike of strikePrices) {
              const strikeY = yScale(strike);
              const tapDistance = Math.abs(y - strikeY);
              if (tapDistance < minStrikeDistance) {
                minStrikeDistance = tapDistance;
                nearestStrike = strike;
              }
            }

            // Prioritize position selection if tapping within 16 pixels of a position
            if (
              nearestPosition &&
              minPositionDistance <= 16 &&
              onPositionSelect
            ) {
              const isSelected =
                selectedPositionGroup?.id === nearestPosition.id;
              onPositionSelect(isSelected ? null : nearestPosition);
            }
            // If no position nearby, select the nearest strike
            // When no strike selected: select nearest (original behavior)
            // When strike already selected: only change if tapping close to a different strike
            else if (
              nearestStrike &&
              onStrikePriceSelect &&
              (!strikePrice ||
                minStrikeDistance <=
                  chartConfig.interaction.strikeSelectDistance)
            ) {
              onStrikePriceSelect(nearestStrike);
            }
            // If tap is elsewhere and we have a strike selected, deselect trade zones
            // But don't deselect if we just finished a drag (within 300ms)
            else if (
              minStrikeDistance > 30 &&
              onTradeZoneDeselect &&
              now - lastDragEnd > 300
            ) {
              onTradeZoneDeselect();
            }
          }
        }

        // Clean up touch state
        lastTouchDistance.current = null;
        touchStartTime.current = 0;
      }}
    />
  );
}
